import type {
  AssistantMessagesPage,
  AssistantSession,
} from '@/lib/api/assistant.api'
import type { InfiniteData } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { assistantApi } from '@/lib/api/assistant.api'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { queryKeys } from '../query-keys'

/**
 * How many conversations the sessions list asks for. A growing WINDOW rather
 * than cursor pages: the list lives under one query key as one array (every
 * cache patch — rename, delete, title — is written against that array, and
 * BennyChat reads it too), so "load more" widens the window and refetches.
 * Module-level on purpose: the window outlives the rail that grew it, so
 * coming back to a conversation doesn't snap the list back to page one.
 */
const SESSIONS_PAGE = 30
let sessionsWindow = SESSIONS_PAGE

export function useAssistantSessionsQuery(options?: { enabled?: boolean }) {
  return useQuery<AssistantSession[]>({
    queryKey: queryKeys.assistant.sessions(),
    queryFn: () => assistantApi.listSessions(sessionsWindow),
    enabled: options?.enabled !== false,
    // Short staleness + focus refetch: conversations are edited from any open
    // tab (rename, delete), and a tab coming back to the foreground must not
    // keep showing rows another tab already removed.
    staleTime: 30 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Infinite-scroll companion to `useAssistantSessionsQuery`: `hasMore` is true
 * while the last fetch filled the window (there may be older conversations),
 * and `loadMore` widens the window by a page and refetches.
 */
export function useAssistantSessionsPager(
  query: ReturnType<typeof useAssistantSessionsQuery>,
) {
  // Re-render after the window grows so `hasMore` reads the new size.
  const [, bump] = useState(0)
  const loaded = query.data?.length ?? 0
  const hasMore = loaded > 0 && loaded >= sessionsWindow
  const loadMore = () => {
    if (!hasMore || query.isFetching) return
    sessionsWindow += SESSIONS_PAGE
    bump(n => n + 1)
    void query.refetch()
  }
  return { hasMore, loadMore, isLoadingMore: hasMore && query.isFetching }
}

export function useAssistantMessagesQuery(
  sessionId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.assistant.messages(sessionId ?? ''),
    queryFn: ({ pageParam }) =>
      assistantApi.getMessages(sessionId!, {
        cursor: pageParam,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    enabled: options?.enabled !== false && !!sessionId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export function useAssistantInvalidation() {
  const queryClient = useQueryClient()

  // Stable reference across renders — callers put these in useCallback deps,
  // so a fresh object literal each render would invalidate every downstream
  // memo (LatestExchangeView → MessageBubble → ReactMarkdown components),
  // causing visible re-mounts of streamed content like ModuleCardList.
  return useMemo(
    () => ({
      invalidateSessions: () =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.sessions(),
        }),
      invalidateMessages: (sessionId: string) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.assistant.messages(sessionId),
        }),
      setMessages: (sessionId: string, page: AssistantMessagesPage) =>
        queryClient.setQueryData<
          InfiniteData<AssistantMessagesPage, string | undefined>
        >(queryKeys.assistant.messages(sessionId), {
          pages: [page],
          pageParams: [undefined],
        }),
      setSessionTitle: (sessionId: string, title: string) => {
        queryClient.setQueryData<AssistantSession[]>(
          queryKeys.assistant.sessions(),
          old =>
            old?.map(s => (s.id === sessionId ? { ...s, title } : s)),
        )
      },
      removeSession: (sessionId: string) => {
        queryClient.removeQueries({
          queryKey: queryKeys.assistant.messages(sessionId),
        })
        queryClient.setQueryData<AssistantSession[]>(
          queryKeys.assistant.sessions(),
          old => old?.filter(s => s.id !== sessionId),
        )
      },
    }),
    [queryClient],
  )
}
